import * as React from 'react';
import {
    useRecordContext,
    useTranslate,
    TextField,
    Loading,
    List,
    SimpleShowLayout,
    FileField,
    Datagrid,
    DeleteWithConfirmButton,
    SimpleForm,
    TextInput,
    SelectInput,
    required,
    FileInput,
    minLength, maxLength, useRefresh
} from 'react-admin';
import {CardActions, Typography} from '@mui/material';
import {OrderReturn} from '../../types';
import {CreateInDialogButton} from "@react-admin/ra-form-layout";
import Grid from '@mui/material/Grid';
import Link from "@mui/material/Link";

const OrderReturnLabels = () => {
    const record = useRecordContext<OrderReturn>();
    const translate = useTranslate();
    const refresh = useRefresh();

    if (!record) {
        return <Loading/>;
    }

    return (
        <SimpleShowLayout record={record} sx={{width: "100%", marginTop: 2}}>
            <Grid container spacing={2}>
                <Grid item xs={8}>
                    <Typography variant="h6" gutterBottom>
                        {translate('resources.order.return.labels.headline')}
                    </Typography>
                </Grid>
                <Grid xs={4} alignItems="center" display="flex" justifyContent="flex-end">
                    <CreateInDialogButton
                        record={{orderReturn: `/order-returns/${record.id}`}}
                        resource={`order-return-labels`}
                        label={`resources.order.return.labels.createLabelButton`}
                        title={`resources.order.return.labels.createLabelButton`}
                        mutationOptions={{
                            onSuccess: () => {
                                refresh();
                            }
                        }}
                    >
                        <SimpleForm>
                            <SelectInput
                                emptyText={'n/a'}
                                source="carrier"
                                fullWidth
                                label="resources.order.return.fields.carrier"
                                name="carrier"
                                choices={
                                    [
                                        {id: 'DHL', name: 'DHL'},
                                        {id: 'DPD', name: 'DPD'},
                                    ]
                                }
                                helperText={false}
                                validate={[required()]}
                            />
                            <TextInput
                                fullWidth
                                source="trackingNumber"
                                name="trackingNumber"
                                label="resources.order.return.fields.trackingNumber"
                                validate={[minLength(5), maxLength(100)]}
                            />
                            <FileInput
                                label={`resources.order.return.labels.file`}
                                source="src"
                                name="src"
                                accept="image/*,.pdf"
                                maxSize={15000000}
                                validate={[required()]}
                            >
                                <FileField source="src" title="label"/>
                            </FileInput>
                        </SimpleForm>
                    </CreateInDialogButton>
                </Grid>
            </Grid>
            <List
                resource="order-return-labels"
                filter={{orderReturn: record.id}}
                pagination={false}
                sort={{field: 'id', order: 'ASC'}}
                perPage={100}
                disableSyncWithLocation
                component="div"
                hasCreate={false}
                exporter={false}
                empty={
                    <Typography variant="body2" color="textSecondary" align="center">
                        {translate('ra.page.empty', {name: translate('resources.order.return.fields.label')})}
                    </Typography>
                }
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={false}
                    sx={{width: '100%'}}
                >
                    <TextField source="carrier" label="resources.order.return.fields.carrier" sortable={false}/>
                    <TextField source="trackingNumber" label="resources.order.return.fields.trackingNumber"
                               sortable={false} emptyText={`n/a`}/>
                    <FileField source="src" title="title" label={`ra.action.download`}/>
                    <DeleteWithConfirmButton
                        translateOptions={{name: "label"}}
                        label={`ra.action.remove`}
                        redirect={false}
                    />
                </Datagrid>
            </List>
            {record?.labelHelperUrl ?
                <CardActions sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                    <Typography variant="caption" color="textSecondary" width="100%" sx={{textAlign: 'center'}}>
                        {translate('resources.order.return.labelHelperLinkTip')}
                    </Typography>
                    <Link href={record.labelHelperUrl} underline="always" target="_blank" rel="noopener noreferrer" sx={{textAlign: 'center', width: '100%'}}>
                        {translate('resources.order.return.labelHelperLink')}
                    </Link>
                </CardActions> : null
            }
        </SimpleShowLayout>
    );
};

export default OrderReturnLabels;
